"use client";

import { useState, useCallback, useMemo } from 'react';
import type {
  ChatMode,
  ChatModeTransitionReason,
  UseChatModeControllerReturn
} from '../types/chat-mode';
import { DEFAULT_CHAT_MODE } from '../const/mode-config';
import {
  ChatModeManagerState,
  createInitialChatModeState,
  transitionToMode,
  restoreToPreviousMode,
  syncStoresForMode,
  saveCurrentModeForRestoration,
} from './chat-mode-state-manager';

/**
 * 채팅 모드 컨트롤러 훅
 *
 * 책임:
 * - 음성/채팅 모드 전환 관리
 * - 이전 모드 저장 및 복원
 * - 모드 전환 시 관련 상태 동기화
 */
export const useChatModeController = (): UseChatModeControllerReturn => {
  // 내부 상태
  const [modeState, setModeState] = useState<ChatModeManagerState>(
    createInitialChatModeState()
  );

  /**
   * 음성 모드로 전환
   */
  const switchToVoiceMode = useCallback(() => {
    setModeState((current) => {
      const newState = transitionToMode(current, 'voice', 'user_initiated');

      if (newState !== current) {
        syncStoresForMode('voice');
        console.log('🎤 [CHAT_MODE_CONTROLLER] 음성 모드로 전환');
      }

      return newState;
    });
  }, []);

  /**
   * 채팅 모드로 전환
   */
  const switchToChatMode = useCallback(() => {
    setModeState((current) => {
      const newState = transitionToMode(current, 'chat', 'user_initiated');

      if (newState !== current) {
        syncStoresForMode('chat');
        console.log('⌨️ [CHAT_MODE_CONTROLLER] 채팅 모드로 전환');
      }

      return newState;
    });
  }, []);

  /**
   * 현재 모드를 이전 모드로 저장
   */
  const savePreviousMode = useCallback(() => {
    setModeState((current) => {
      console.log('💾 [CHAT_MODE_CONTROLLER] 현재 모드 저장:', current.current);
      return saveCurrentModeForRestoration(current);
    });
  }, []);

  /**
   * 이전 모드로 복원
   * @returns 복원 성공 여부
   */
  const restorePreviousMode = useCallback((): boolean => {
    let restored = false;

    setModeState((current) => {
      if (!current.previous) {
        console.log('⚠️ [CHAT_MODE_CONTROLLER] 복원할 이전 모드 없음');
        return current;
      }

      const newState = restoreToPreviousMode(current);

      if (newState !== current) {
        syncStoresForMode(newState.current);
        console.log('🔄 [CHAT_MODE_CONTROLLER] 이전 모드로 복원:', newState.current);
        restored = true;
      }

      return newState;
    });

    return restored;
  }, []);

  // Computed values
  const isVoiceMode = modeState.current === 'voice';
  const isChatMode = modeState.current === 'chat';

  return {
    // 현재 상태
    currentMode: modeState.current,
    previousMode: modeState.previous,

    // 모드 전환
    switchToVoiceMode,
    switchToChatMode,

    // 상태 저장/복원
    savePreviousMode,
    restorePreviousMode,

    // 모드 체크
    isVoiceMode,
    isChatMode,
  };
};
